import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setPageUserReports,
  toggleNeedRefreshUserReports,
} from '../../store/userReportsTabSlice.js';
import {
  setViewedUser,
  setModalViewActualTab,
  setEditModalOpen,
} from '../../store/userTabSlice.js';
import {
  getHeaders,
  headerWidths,
  tabsConfig,
  empty,
} from './userReportsTableConfig.js';
import { fetchUserReports } from './userReportsTableService.js';

import Table from '../../components/list2/table.jsx';
import Pagination from '../../components/components/pagination/pagination.jsx';
import Menu from '../../components/menu/menu.jsx';
import Content from '../../components/content/content.jsx';
import LineWithButtons from '../../components/components/line_with_buttons/lineWithButtons.jsx';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import ApiService from '../../components/api_services/apiService.jsx';
import Modal from '../../components/components/modal/modal.jsx';
import ViewUser from '../users/viewUser.jsx';
import './UserReports.css';

const parseJsonSafe = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const raw = value.trim();
  if (!raw) {
    return null;
  }

  const attempts = [raw];
  if (raw.includes('""')) {
    attempts.push(raw.replace(/""/g, '"'));
  }

  for (const candidate of attempts) {
    try {
      let parsed = JSON.parse(candidate);
      let guard = 0;
      while (typeof parsed === 'string' && guard < 3) {
        parsed = JSON.parse(parsed);
        guard += 1;
      }
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (error) {
      // next
    }
  }

  return null;
};

const normalizeMediaPath = (value, fallbackPrefix = '/attachment/') => {
  const raw = String(value || '').trim().replace(/^"+|"+$/g, '');
  if (!raw) {
    return '';
  }

  const attachmentIndex = raw.indexOf('/attachment/');
  if (attachmentIndex >= 0) {
    return raw.substring(attachmentIndex);
  }

  const imageIndex = raw.indexOf('/images/');
  if (imageIndex >= 0) {
    const imagePath = raw.substring(imageIndex);
    return imagePath.replace('/images/', '/proxy-images/');
  }

  if (raw.startsWith('/attachment/')) {
    return raw;
  }

  if (raw.startsWith('/images/')) {
    return raw.replace('/images/', '/proxy-images/');
  }

  return `${fallbackPrefix}${raw}`;
};

const normalizePublicImageUrl = (value) => {
  const raw = String(value || '').trim().replace(/^"+|"+$/g, '');
  if (!raw) {
    return '';
  }

  const imageIndex = raw.indexOf('/images/');
  if (imageIndex >= 0) {
    return `https://api.teamme.pl${raw.substring(imageIndex)}`;
  }

  if (raw.startsWith('/images/')) {
    return `https://api.teamme.pl${raw}`;
  }

  return `https://api.teamme.pl/images/${raw}`;
};

const getMessageData = (message) => {
  const messageJsonParsed = parseJsonSafe(message?.messageJson);
  const payloadParsed = parseJsonSafe(message?.payload) || parseJsonSafe(message?.messagePayload);
  const rootParsed = parseJsonSafe(message?.json) || parseJsonSafe(message?.rawJson);
  const nestedDataParsed = parseJsonSafe(message?.data) || parseJsonSafe(message?.content);

  const source = {
    ...(rootParsed || {}),
    ...(payloadParsed || {}),
    ...(messageJsonParsed || {}),
    ...(nestedDataParsed || {}),
    ...(message || {}),
  };

  const messageText = source.message || '';
  const senderName = source.senderName || '-';
  const senderPhotoUrl = normalizePublicImageUrl(source.senderPhotoUrl);
  const recipientName = source.recipientName || '-';
  const recipientPhotoUrl = normalizePublicImageUrl(source.recipientPhotoUrl);
  const senderUUID = source.senderUUID || source.senderUuid || '-';
  const recipientUUID = source.recipientUUID || source.recipientUuid || '-';
  const groupId = source.groupId ?? source.chatRoomId ?? null;
  const messageType = source.messageType || '-';
  const attachmentType = String(source.attachmentType || 'NO_ATTACHMENT').toUpperCase().trim();
  const attachmentUrl = normalizeMediaPath(
    source.attachmentUrl || source.attachmentURL || source.attachment || '',
    '/attachment/'
  );
  const sentAt = source.sentAt || message?.sentAt || '-';
  const messageId = source.id || message?.id || '-';

  return {
    messageId,
    messageText,
    messageType,
    senderName,
    senderPhotoUrl,
    senderUUID,
    recipientName,
    recipientPhotoUrl,
    recipientUUID,
    groupId,
    attachmentType,
    attachmentUrl,
    sentAt,
  };
};

const getInitial = (value) => (value ? value.trim().charAt(0).toUpperCase() : '?');
const isImageAttachment = (attachmentType) => String(attachmentType || '').toUpperCase().includes('IMAGE');
const isAudioAttachment = (attachmentType) => {
  const normalized = String(attachmentType || '').toUpperCase();
  return normalized.includes('VOICE') || normalized.includes('AUDIO');
};

const renderUserInfoCell = (userId, profile, onOpenUserDetails) => {
  if (!userId) {
    return '-';
  }

  const name = profile?.name || userId;
  const age = profile?.age ?? '-';
  const stars = profile?.starsCounter ?? '-';

  return (
    <div className="user-report-cell">
      <div className="user-report-cell-name">{name}</div>
      <details className="user-report-cell-details">
        <summary>Szczegóły</summary>
        <div>Wiek: {age}</div>
        <div>Ocena: {stars}</div>
      </details>
      <button
        type="button"
        className="user-report-cell-link"
        onClick={() => onOpenUserDetails(userId)}
      >
        Otwórz szczegóły użytkownika
      </button>
    </div>
  );
};

function UserReports() {
  const dispatch = useDispatch();
  const currentPage = useSelector((state) => state.userReports.currentPage);
  const totalPages = useSelector((state) => state.userReports.maxPages);
  const rows = useSelector((state) => state.userReports.rows);
  const needRefresh = useSelector((state) => state.userReports.needRefresh);
  const [selectedReportMessages, setSelectedReportMessages] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReportEndpointKey, setSelectedReportEndpointKey] = useState('direct');
  const [attachmentObjectUrls, setAttachmentObjectUrls] = useState({});
  const [resolveModalReportId, setResolveModalReportId] = useState(null);
  const [isResolvingReport, setIsResolvingReport] = useState(false);
  const [deletingMessageIds, setDeletingMessageIds] = useState({});
  const [resolveForm, setResolveForm] = useState({
    messagesDeleted: false,
    accountBlocked: false,
  });

  const truncatePreview = (text, maxLength = 75) => {
    if (!text) {
      return '';
    }
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getMessagePreview = (message) => {
    if (!message) {
      return 'Brak danych wiadomości';
    }

    const { messageText, attachmentType } = getMessageData(message);

    if (messageText && attachmentType === 'NO_ATTACHMENT') {
      return truncatePreview(messageText);
    }

    if (isImageAttachment(attachmentType)) {
      return '[Załącznik graficzny]';
    }

    if (isAudioAttachment(attachmentType)) {
      return '[Załącznik dźwiękowy]';
    }

    if (attachmentType && attachmentType !== 'NO_ATTACHMENT') {
      return `[Załącznik: ${attachmentType}]`;
    }

    if (messageText) {
      return truncatePreview(messageText);
    }

    return '[Brak treści]';
  };

  const openMessagesModal = (reportId, messages, endpointKey = 'direct') => {
    setSelectedReportId(reportId);
    setSelectedReportEndpointKey(endpointKey);
    setSelectedReportMessages(messages || []);
  };

  const closeMessagesModal = () => {
    setSelectedReportId(null);
    setSelectedReportEndpointKey('direct');
    setSelectedReportMessages([]);
    setDeletingMessageIds({});
  };

  const selectedAttachmentUrls = useMemo(() => {
    const urls = selectedReportMessages
      .map((message) => getMessageData(message).attachmentUrl)
      .filter(Boolean);
    return [...new Set(urls)];
  }, [selectedReportMessages]);

  useEffect(() => {
    let isMounted = true;
    const localObjectUrls = [];

    const loadProtectedAttachments = async () => {
      if (selectedAttachmentUrls.length === 0) {
        setAttachmentObjectUrls({});
        return;
      }

      const entries = await Promise.all(
        selectedAttachmentUrls.map(async (attachmentUrl) => {
          try {
            const blob = await ApiService.getProtectedAttachmentBlob(attachmentUrl);
            const objectUrl = URL.createObjectURL(blob);
            localObjectUrls.push(objectUrl);
            return [attachmentUrl, { objectUrl, mimeType: blob?.type || '' }];
          } catch (error) {
            return [attachmentUrl, null];
          }
        })
      );

      if (!isMounted) {
        localObjectUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }

      setAttachmentObjectUrls(Object.fromEntries(entries.filter(([, value]) => !!value?.objectUrl)));
    };

    loadProtectedAttachments();

    return () => {
      isMounted = false;
      localObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedAttachmentUrls]);

  const openUserDetailsModal = (userId) => {
    dispatch(setViewedUser(userId));
    dispatch(setModalViewActualTab('Dane podstawowe'));
    dispatch(setEditModalOpen(true));
  };

  const getDeleteEndpointKeyForMessage = (message) => {
    if (selectedReportEndpointKey === 'chat' || selectedReportEndpointKey === 'direct') {
      return selectedReportEndpointKey;
    }

    const messageType = String(
      message?.messageType || message?.type || message?.contentType || ''
    ).toUpperCase();

    return messageType === 'GROUP' || messageType === 'CHAT' ? 'chat' : 'direct';
  };

  const handleDeleteMessage = async (message) => {
    const messageId = message?.id;
    if (!messageId) {
      return;
    }

    const endpointKey = getDeleteEndpointKeyForMessage(message);
    setDeletingMessageIds((prev) => ({ ...prev, [messageId]: true }));

    try {
      if (endpointKey === 'chat') {
        await ApiService.deleteChatMessageById(messageId);
      } else {
        await ApiService.deleteSingleMessageById(messageId);
      }

      setSelectedReportMessages((prev) => prev.filter((item) => item?.id !== messageId));
      dispatch(toggleNeedRefreshUserReports());
    } catch (error) {
      console.error('Błąd podczas usuwania wiadomości:', error);
    } finally {
      setDeletingMessageIds((prev) => {
        const updated = { ...prev };
        delete updated[messageId];
        return updated;
      });
    }
  };

  const openResolveModal = (reportId) => {
    setResolveModalReportId(reportId);
    setResolveForm({
      messagesDeleted: false,
      accountBlocked: false,
    });
  };

  const closeResolveModal = () => {
    if (isResolvingReport) {
      return;
    }

    setResolveModalReportId(null);
    setResolveForm({
      messagesDeleted: false,
      accountBlocked: false,
    });
  };

  const handleResolveOptionChange = (event) => {
    const { name, checked } = event.target;
    setResolveForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleResolveReport = async () => {
    if (resolveModalReportId === null) {
      return;
    }

    try {
      setIsResolvingReport(true);
      await ApiService.resolveUserReport(
        resolveModalReportId,
        true,
        resolveForm.accountBlocked,
        resolveForm.messagesDeleted
      );
      setResolveModalReportId(null);
      setResolveForm({
        messagesDeleted: false,
        accountBlocked: false,
      });
      dispatch(toggleNeedRefreshUserReports());
    } catch (error) {
      console.error('Błąd podczas oznaczania zgłoszenia jako rozwiązane:', error);
    } finally {
      setIsResolvingReport(false);
    }
  };

  useEffect(() => {
    fetchUserReports(currentPage, dispatch);
  }, [currentPage, needRefresh, dispatch]);

  const headers = getHeaders();
  const renderedRows = rows.map((row) => [
    row.id,
    renderUserInfoCell(row.reportedBy, row.reportedByProfile, openUserDetailsModal),
    renderUserInfoCell(row.reportedUser, row.reportedUserProfile, openUserDetailsModal),
    row.reportDescription,
    <div className="report-messages-preview">
      {row.reportMessages && row.reportMessages.length > 0 ? (
        <>
          {row.reportMessages.slice(0, 2).map((message) => (
            <div key={message.id} className="report-messages-preview-item">
              {getMessagePreview(message)}
            </div>
          ))}
            <button
              type="button"
              className="user-report-more-button"
              onClick={() => openMessagesModal(row.id, row.reportMessages, row.endpointKey)}
            >
              Więcej
            </button>
        </>
      ) : (
        <span className="report-messages-preview-item">Brak treści do podglądu</span>
      )}
    </div>,
    row.addedAt,
    <button
      type="button"
      className="user-report-resolve-button"
      onClick={() => openResolveModal(row.id)}
    >
      Oznacz jako rozwiązane
    </button>,
  ]);

  const paginationProps = {
    reduxSelector: (state) => state.userReports.currentPage,
    reduxAction: setPageUserReports,
    totalPages: totalPages,
    maxVisibleButtons: 5,
  };

  const rightSideConfig = [
    {
      component: Pagination,
      props: paginationProps,
    },
  ];

  return (
    <div>
      <Menu />
      <Content>
        <ViewUser />
        <LineWithButtons tabs={tabsConfig()} buttons={empty} />
        <div className="table-container">
          <Table
            headers={headers}
            rowsClass='row-user-reports'
            cellClass='cell-user-reports'
            rows={renderedRows}
            columnWidths={headerWidths}
            idViewing={true}
            idArrayId={0}
          />
          <UniversalLineWithComponents leftSide={empty} rightSide={rightSideConfig} />
        </div>

        <Modal isOpen={selectedReportId !== null} onClose={closeMessagesModal} className="modal-content-2">
          <h2>Szczegóły wiadomości zgłoszenia #{selectedReportId}</h2>
          {selectedReportMessages.length > 0 ? (
            <div className="report-messages-modal-scroll">
              <div className="report-messages-modal-list">
              {selectedReportMessages.map((message) => {
                const data = getMessageData(message);
                const isDeleting = !!deletingMessageIds[message.id];
                const attachmentMeta = data.attachmentUrl
                  ? attachmentObjectUrls[data.attachmentUrl] || null
                  : null;
                const protectedAttachmentSrc = attachmentMeta?.objectUrl || '';
                const protectedAttachmentMimeType = attachmentMeta?.mimeType || '';
                const isImage = isImageAttachment(data.attachmentType) || protectedAttachmentMimeType.startsWith('image/');
                const isAudio = isAudioAttachment(data.attachmentType) || protectedAttachmentMimeType.startsWith('audio/');
                return (
                  <div key={message.id} className="report-message-card">
                      <div><strong>ID:</strong> {data.messageId}</div>

                      <div className="report-message-users">
                        <div className="report-message-user">
                          {data.senderPhotoUrl ? (
                            <img src={data.senderPhotoUrl} alt={data.senderName} className="report-avatar" />
                          ) : (
                            <div className="report-avatar report-avatar-fallback">{getInitial(data.senderName)}</div>
                          )}
                          <span><strong>Nadawca:</strong> {data.senderName}</span>
                        </div>

                        {data.messageType === 'GROUP' ? (
                          <div className="report-message-user">
                            <div className="report-avatar report-avatar-fallback">#</div>
                            <span><strong>Grupa:</strong> {data.groupId ?? '-'}</span>
                          </div>
                        ) : (
                          <div className="report-message-user">
                            {data.recipientPhotoUrl ? (
                              <img src={data.recipientPhotoUrl} alt={data.recipientName} className="report-avatar" />
                            ) : (
                              <div className="report-avatar report-avatar-fallback">{getInitial(data.recipientName)}</div>
                            )}
                            <span><strong>Odbiorca:</strong> {data.recipientName}</span>
                          </div>
                        )}
                      </div>

                    <div><strong>Wysłano:</strong> {data.sentAt}</div>
                    <div><strong>Treść:</strong> {data.messageText || '[Brak tekstu]'}</div>
                      <div><strong>Typ załącznika:</strong> {data.attachmentType}</div>

                    {isImage && protectedAttachmentSrc && (
                      <div className="report-attachment-container">
                        <img src={protectedAttachmentSrc} alt="Załącznik graficzny" className="report-attachment-image" />
                      </div>
                    )}

                    {isAudio && protectedAttachmentSrc && (
                      <div className="report-attachment-container">
                        <audio controls preload="none" className="report-attachment-audio">
                          <source src={protectedAttachmentSrc} type={protectedAttachmentMimeType || 'audio/mp4'} />
                        </audio>
                      </div>
                    )}

                    {!isImage && !isAudio && !!data.attachmentUrl && protectedAttachmentSrc && (
                      <div className="report-attachment-container">
                        <div className="report-attachment-missing">Nie rozpoznano typu pliku, próba podglądu:</div>
                        <img src={protectedAttachmentSrc} alt="Podgląd załącznika" className="report-attachment-image" />
                        <audio controls preload="none" className="report-attachment-audio">
                          <source src={protectedAttachmentSrc} type={protectedAttachmentMimeType || 'audio/mp4'} />
                        </audio>
                      </div>
                    )}

                    <button
                      type="button"
                      className="report-message-delete-button"
                      onClick={() => handleDeleteMessage(message)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Usuwanie...' : 'Usuń'}
                    </button>

                  </div>
                );
              })}
              </div>
            </div>
          ) : (
            <div>Brak wiadomości do wyświetlenia.</div>
          )}
        </Modal>

        <Modal isOpen={resolveModalReportId !== null} onClose={closeResolveModal}>
          <h2>Potwierdź rozwiązanie zgłoszenia</h2>
          <p className="resolve-report-confirmation-text">
            Czy na pewno chcesz oznaczyć zgłoszenie #{resolveModalReportId} jako rozwiązane?
          </p>

          <label className="resolve-report-checkbox">
            <input
              type="checkbox"
              name="messagesDeleted"
              checked={resolveForm.messagesDeleted}
              onChange={handleResolveOptionChange}
              disabled={isResolvingReport}
            />
            <span>Wiadomości zostały usunięte</span>
          </label>

          <label className="resolve-report-checkbox">
            <input
              type="checkbox"
              name="accountBlocked"
              checked={resolveForm.accountBlocked}
              onChange={handleResolveOptionChange}
              disabled={isResolvingReport}
            />
            <span>Konto zostało zablokowane</span>
          </label>

          <div className="resolve-report-actions">
            <button type="button" onClick={closeResolveModal} disabled={isResolvingReport}>
              Anuluj
            </button>
            <button
              type="button"
              className="resolve-report-confirm-button"
              onClick={handleResolveReport}
              disabled={isResolvingReport}
            >
              {isResolvingReport ? 'Zapisywanie...' : 'Potwierdź'}
            </button>
          </div>
        </Modal>
      </Content>
    </div>
  );
}

export default UserReports;
